export type EditForClarityCleanupSettings = {
    removeFillerWords: boolean;
    tightenSilenceGaps: boolean;
    removeRetakes: boolean;
};

export type EditForClarityCleanupStepId =
    | "remove_filler_words"
    | "tighten_silence_gaps"
    | "remove_retakes";

export type EditForClarityCleanupStepState =
    | "pending"
    | "running"
    | "completed"
    | "skipped"
    | "failed"
    | "stopped";

export type EditForClarityCleanupStep = {
    id: EditForClarityCleanupStepId;
    label: string;
    state: EditForClarityCleanupStepState;
    detail: string;
};

export type EditForClarityCleanupProgress = {
    phase: "running" | "completed" | "failed" | "stopped";
    currentStepId: EditForClarityCleanupStepId | null;
    startedAtMs: number;
    completedAtMs?: number;
    fillerWordsRemoved: number;
    retakesDetected: number;
    silenceSecondsRemoved: number;
    errorMessage?: string | null;
    steps: EditForClarityCleanupStep[];
};

export type EditForClarityCleanupMetricsPatch = Partial<
    Pick<EditForClarityCleanupProgress, "fillerWordsRemoved" | "retakesDetected" | "silenceSecondsRemoved">
>;

export type EditForClarityCleanupStepResult = {
    detail?: string;
    metrics?: EditForClarityCleanupMetricsPatch;
};

export type EditForClarityCleanupStepContext = {
    isCancelled: () => boolean;
    throwIfCancelled: () => void;
    updateMetrics: (patch: EditForClarityCleanupMetricsPatch) => void;
};

type EditForClarityCleanupStepExecutor = (
    context: EditForClarityCleanupStepContext,
) => Promise<EditForClarityCleanupStepResult | void>;

const cloneSteps = (steps: EditForClarityCleanupStep[]): EditForClarityCleanupStep[] =>
    steps.map((step) => ({ ...step }));

const buildInitialSteps = (settings: EditForClarityCleanupSettings): EditForClarityCleanupStep[] => [
    {
        id: "remove_retakes",
        label: "Remove retakes",
        state: settings.removeRetakes ? "pending" : "skipped",
        detail: settings.removeRetakes ? "Queued" : "Disabled",
    },
    {
        id: "remove_filler_words",
        label: "Remove filler words",
        state: settings.removeFillerWords ? "pending" : "skipped",
        detail: settings.removeFillerWords ? "Queued" : "Disabled",
    },
    {
        id: "tighten_silence_gaps",
        label: "Tighten silence gaps",
        state: settings.tightenSilenceGaps ? "pending" : "skipped",
        detail: settings.tightenSilenceGaps ? "Queued" : "Disabled",
    },
];

export const createInitialEditForClarityCleanupProgress = (
    settings: EditForClarityCleanupSettings,
): EditForClarityCleanupProgress => ({
    phase: "running",
    currentStepId: null,
    startedAtMs: Date.now(),
    fillerWordsRemoved: 0,
    retakesDetected: 0,
    silenceSecondsRemoved: 0,
    errorMessage: null,
    steps: buildInitialSteps(settings),
});

const markStep = (
    progress: EditForClarityCleanupProgress,
    stepId: EditForClarityCleanupStepId,
    nextState: EditForClarityCleanupStepState,
    detail: string,
): EditForClarityCleanupProgress => ({
    ...progress,
    currentStepId: nextState === "running" ? stepId : progress.currentStepId,
    steps: progress.steps.map((step) =>
        step.id === stepId
            ? {
                  ...step,
                  state: nextState,
                  detail,
              }
            : step,
    ),
});

export const runEditForClarityCleanupStub = async (args: {
    settings: EditForClarityCleanupSettings;
    onProgress?: (progress: EditForClarityCleanupProgress) => void;
    isCancelled?: () => boolean;
    executeRemoveRetakes?: EditForClarityCleanupStepExecutor;
    executeRemoveFillerWords?: EditForClarityCleanupStepExecutor;
    executeTightenSilenceGaps?: EditForClarityCleanupStepExecutor;
}): Promise<EditForClarityCleanupProgress> => {
    const STOP_ERROR_CODE = "__edit_for_clarity_stopped__";
    const isCancelled = () => Boolean(args.isCancelled?.());
    const throwIfCancelled = () => {
        if (isCancelled()) {
            const err: any = new Error("Edit for Clarity stopped.");
            err.code = STOP_ERROR_CODE;
            throw err;
        }
    };

    let progress = createInitialEditForClarityCleanupProgress(args.settings);
    const emitProgress = () => {
        args.onProgress?.({ ...progress, steps: cloneSteps(progress.steps) });
    };
    emitProgress();

    const updateMetrics = (patch: EditForClarityCleanupMetricsPatch) => {
        if (!patch) return;
        progress = {
            ...progress,
            fillerWordsRemoved: Number(
                patch.fillerWordsRemoved ?? progress.fillerWordsRemoved,
            ),
            retakesDetected: Number(
                patch.retakesDetected ?? progress.retakesDetected,
            ),
            silenceSecondsRemoved: Number(
                patch.silenceSecondsRemoved ?? progress.silenceSecondsRemoved,
            ),
        };
        emitProgress();
    };

    const runStep = async (
        stepId: EditForClarityCleanupStepId,
        body: EditForClarityCleanupStepExecutor | undefined,
        completedDetail: string,
    ) => {
        const step = progress.steps.find((s) => s.id === stepId);
        if (!step || step.state === "skipped") return;

        throwIfCancelled();

        progress = markStep(progress, stepId, "running", "Running...");
        emitProgress();

        const result = await (body
            ? body({ isCancelled, throwIfCancelled, updateMetrics })
            : Promise.resolve(undefined));

        if (result?.metrics) {
            updateMetrics(result.metrics);
        }

        if (isCancelled()) {
            progress = markStep(
                progress,
                stepId,
                "stopped",
                result?.detail || "Stopped",
            );
            emitProgress();
            throwIfCancelled();
        }

        progress = markStep(
            progress,
            stepId,
            "completed",
            result?.detail || completedDetail,
        );
        emitProgress();
    };

    try {
        await runStep(
            "remove_retakes",
            args.executeRemoveRetakes ??
                (async () => {
                    // TODO: Retake detection + cut application.
                    await Promise.resolve();
                    return { detail: "Stubbed" };
                }),
            "Done",
        );

        await runStep(
            "remove_filler_words",
            args.executeRemoveFillerWords ??
                (async () => {
                    // TODO:
                    // 1) Activate filler-word scan.
                    // 2) Force smooth cuts on.
                    // 3) Remove all occurrences and update fillerWordsRemoved in progress.
                    await Promise.resolve();
                    return { detail: "Done" };
                }),
            "Done",
        );

        await runStep(
            "tighten_silence_gaps",
            args.executeTightenSilenceGaps ??
                (async () => {
                    // TODO:
                    // 1) Activate gap scan.
                    // 2) Only include gaps > 3s.
                    // 3) Shorten each to 3s and update silenceSecondsRemoved in progress.
                    await Promise.resolve();
                    return { detail: "Done" };
                }),
            "Done",
        );

        progress = {
            ...progress,
            phase: "completed",
            currentStepId: null,
            completedAtMs: Date.now(),
        };
        emitProgress();
        return progress;
    } catch (error: any) {
        const isStopped =
            String(error?.code || "") === STOP_ERROR_CODE ||
            String(error?.message || "").toLowerCase().includes("stopped");
        if (isStopped || isCancelled()) {
            progress = {
                ...progress,
                phase: "stopped",
                currentStepId: null,
                completedAtMs: Date.now(),
                errorMessage: null,
                steps: progress.steps.map((step) => {
                    if (step.state === "running") {
                        return {
                            ...step,
                            state: "stopped",
                            detail: "Stopped",
                        };
                    }
                    if (step.state === "pending") {
                        return {
                            ...step,
                            state: "skipped",
                            detail: "Skipped",
                        };
                    }
                    return step;
                }),
            };
            emitProgress();
            return progress;
        }

        const msg = String(error?.message ?? error ?? "Edit for Clarity cleanup failed.");
        progress = {
            ...progress,
            phase: "failed",
            currentStepId: null,
            completedAtMs: Date.now(),
            errorMessage: msg,
            steps: progress.steps.map((step) =>
                step.state === "running"
                    ? {
                          ...step,
                          state: "failed",
                          detail: msg,
                      }
                    : step,
            ),
        };
        emitProgress();
        return progress;
    }
};
